
import {
  defineComponent, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import SelecionarFormaPagamento from '@/components/Cadastros/Financeiro/SelecionarFormaPagamento.vue';
import SelecionarCenarioFiscal from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCenarioFiscal.vue';
import ServicoTipoOperacao from '@/servicos/Cadastros/ServicoTipoOperacao';
import { ITipoOperacao, ITipoOperacaoEmpresa, ITipoOperacaoTipoMovimentacao } from '@/models/Entidades/Cadastros/ITipoOperacao';
import SelecionarSimNao from '@/core/components/Tela/SelecionarSimNao.vue';
import SelecionarEmpresa from '@/components/MeuSistema/Empresas/SelecionarEmpresa.vue';
import SelecionarTipoMovimentoComercial from '@/components/Compartilhados/SelecionarTipoMovimentoComercial.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarTipoFrete from '@/components/Compartilhados/SelecionarTipoFrete.vue';
import SelecionarTabelaPreco from '@/components/Cadastros/Precificacao/TabelasPreco/SelecionarTabelaPreco.vue';
import SelecionarTipoPreco from '@/components/Cadastros/Precificacao/SelecionarTipoPreco.vue';

export default defineComponent({
  name: 'TipoOperacaoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    RequisicaoModal,
    Icone,
    SelecionarFormaPagamento,
    SelecionarCenarioFiscal,
    SelecionarSimNao,
    SelecionarEmpresa,
    SelecionarTipoMovimentoComercial,
    SelecionarTipoFrete,
    SelecionarTabelaPreco,
    SelecionarTipoPreco,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, verificaConceitoParaApresentarEmpresas, defineEmpresasSelecionadasCadastroCompartilhado,
      preencherPermissoesDados, filtrarPermissaoDadosUsuarioMultiEmpresas, apresentarMensagemSucesso, apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso, sincronizarRegistro, defineTextoPadraoBotoes,
    } = useModalBase(props, emit);
    const servicoTipoOperacao = new ServicoTipoOperacao();
    telaBase.identificadorRecurso = 'CADASTRO_TIPOS_OPERACAO';
    telaBase.identificadorPermissao = 'PER_CADASTRO_TIPOS_OPERACAO';

    const state = reactive({
      tipoOperacao: {} as ITipoOperacao,
      tiposMovimentos: [] as number[],
    });

    function limparTela() {
      state.tipoOperacao = {} as ITipoOperacao;
      state.tipoOperacao.codigoCenarioFiscal = 0;
      state.tipoOperacao.codigoFormaPagamento = 0;
      state.tipoOperacao.movimentarEstoque = true;
      state.tipoOperacao.gerarComissao = true;
      state.tipoOperacao.padraoFrenteCaixa = false;
      state.tipoOperacao.codigoTabelaPreco = 0;
      state.tipoOperacao.codigoTipoPreco = 0;
      state.tipoOperacao.empresas = [] as ITipoOperacaoEmpresa[];
      state.tiposMovimentos = [];
    }

    function obterTiposMovimentacao() {
      state.tipoOperacao.tiposMovimentacao = [];
      state.tiposMovimentos.forEach((t) => {
        const tipoMovimentacao = {} as ITipoOperacaoTipoMovimentacao;
        tipoMovimentacao.codigoTipoOperacao = state.tipoOperacao.codigo;
        tipoMovimentacao.tipoMovimentacao = t;
        state.tipoOperacao.tiposMovimentacao.push(tipoMovimentacao);
      });
    }

    async function verificarCampos() {
      if (state.tipoOperacao.descricao === '') {
        apresentarMensagemAlerta('A descrição deve ser informada.');
        return false;
      }
      if (state.tipoOperacao.codigoCenarioFiscal === 0) {
        apresentarMensagemAlerta('O cenário fiscal deve ser informado.');
        return false;
      }
      if (state.tiposMovimentos.length === 0) {
        apresentarMensagemAlerta('Pelo menos um tipo de movimentação deve ser informado.');
        return false;
      }
      if (state.tipoOperacao.padraoFrenteCaixa) {
        if (state.tipoOperacao.codigoFormaPagamento === 0) {
          apresentarMensagemAlerta('Para o tipo de operação padrão do frente de caixa, é obrigatório selecionar uma forma de pagamento');
          return false;
        }
        if (state.tipoOperacao.codigoTabelaPreco === 0) {
          apresentarMensagemAlerta('Para o tipo de operação padrão do frente de caixa, é obrigatório selecionar uma tabela de preço');
          return false;
        }
        if (state.tipoOperacao.codigoTipoPreco === 0) {
          apresentarMensagemAlerta('Para o tipo de operação padrão do frente de caixa, é obrigatório selecionar um tipo de preço');
          return false;
        }
      }
      return true;
    }

    async function salvar(salvarNovo: boolean) {
      const retornoVerificacao = verificarCampos();
      if (!retornoVerificacao) {
        return;
      }

      obterTiposMovimentacao();
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };
      state.tipoOperacao.empresas = [];
      if (telaBase.empresasSelecionadas.length > 0) {
        telaBase.empresasSelecionadas.forEach((codigoEmpresa) => {
          const empresa = {} as ITipoOperacaoEmpresa;
          empresa.codigo = 0;
          empresa.codigoTipoOperacao = state.tipoOperacao.codigo;
          empresa.codigoEmpresa = codigoEmpresa;
          state.tipoOperacao.empresas.push(empresa);
        });
      }
      apresentarBarraProgresso();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoTipoOperacao.incluir(state.tipoOperacao);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoTipoOperacao.alterar(state.tipoOperacao);
      }
      ocultarBarraProgresso();
      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          limparTela();
          const telaOperacao: ITelaOperacao = props.operacao;
          telaOperacao.codigoRegistro = 0;
          telaOperacao.tipoPermissaoDados = EPermissaoDados.Incluir;
          modalBase.computedOperacao = telaOperacao;
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      limparTela();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);
        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.tipoOperacao = await servicoTipoOperacao.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          telaBase.empresasSelecionadas = [];
          if (state.tipoOperacao.empresas.length > 0) {
            state.tipoOperacao.empresas.forEach((empresa) => {
              telaBase.empresasSelecionadas.push(empresa.codigoEmpresa);
            });
          }
          if (UtilitarioGeral.validaLista(state.tipoOperacao.tiposMovimentacao)) {
            state.tipoOperacao.tiposMovimentacao.forEach((item) => {
              state.tiposMovimentos.push(item.tipoMovimentacao);
            });
          }
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }
      }

      telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
      verificaConceitoParaApresentarEmpresas();
      telaBase.carregando = false;
    });

    return {
      props,
      state,
      telaBase,
      modalBase,
      EPermissaoDados,
      apresentarRetornoRequisicao,
      salvar,
    };
  },
});
